import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Globe2, Users, MapPin, BadgeCheck } from "lucide-react";
import {
  DashboardShell,
  PageHeading,
  ChartCard,
  tooltipStyle,
  fmt,
  LoadingPanel,
} from "@/components/DashboardShell";
import { useTradeData } from "@/components/DataProvider";
import { countBy, perspectiveSnapshot } from "@/lib/analytics";
import { leadRca, perspectiveConfig } from "@/lib/perspective";

export const Route = createFileRoute("/markets")({
  head: () => ({ meta: [{ title: "Markets — Ready4Trade" }] }),
  component: MarketsPage,
});

function MarketsPage() {
  const { leads, aggregates, isLoading, perspective } = useTradeData();
  const config = perspectiveConfig(perspective);
  const snapshot = useMemo(
    () => (leads ? perspectiveSnapshot(leads, perspective) : null),
    [leads, perspective],
  );
  const buyerCountries = useMemo(
    () =>
      leads
        ? countBy(
            leads.filter((lead) => (leadRca(lead, perspective) ?? 0) > 1).map((lead) => lead.bf),
            20,
          )
        : [],
    [leads, perspective],
  );
  const comparison = useMemo(() => {
    if (!snapshot) return [];
    const names = new Set([
      ...snapshot.allMarkets.slice(0, 15).map((row) => row.name),
      ...snapshot.alignedMarkets.slice(0, 15).map((row) => row.name),
    ]);
    return [...names]
      .map((name) => ({
        name,
        "All leads": snapshot.allMarkets.find((row) => row.name === name)?.count ?? 0,
        "RCA > 1": snapshot.alignedMarkets.find((row) => row.name === name)?.count ?? 0,
      }))
      .sort((a, b) => b["RCA > 1"] - a["RCA > 1"] || b["All leads"] - a["All leads"])
      .slice(0, 15);
  }, [snapshot]);

  if (isLoading || !leads || !aggregates || !snapshot)
    return (
      <DashboardShell>
        <LoadingPanel />
      </DashboardShell>
    );

  return (
    <DashboardShell meta={aggregates.meta}>
      <PageHeading
        title={`${config.flag} Market intelligence`}
        subtitle={`Compare total buyer demand with markets linked to products where ${config.country} has pooled RCA above 1.`}
        badge={`${fmt(snapshot.alignedMarkets.length)} RCA-aligned markets`}
      />

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard
          title="Delivery markets: all versus RCA-aligned"
          subtitle="RCA-aligned means pooled HS6 RCA above 1 for the selected exporter"
          icon={<Globe2 className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={520}>
            <BarChart data={comparison} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <YAxis
                dataKey="name"
                type="category"
                width={145}
                tick={{ fontSize: 10, fill: "#334155" }}
                stroke="#cbd5e1"
              />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={tooltipStyle} />
              <Legend />
              <Bar dataKey="All leads" fill="#cbd5e1" radius={[0, 5, 5, 0]} />
              <Bar dataKey="RCA > 1" fill="#10b981" radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Buyer-company countries for RCA-aligned products"
          subtitle="Where the importing company is based; destination may be different"
          icon={<Users className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={520}>
            <BarChart
              data={buyerCountries.slice(0, 18)}
              layout="vertical"
              margin={{ left: 10, right: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <YAxis
                dataKey="name"
                type="category"
                width={145}
                tick={{ fontSize: 10, fill: "#334155" }}
                stroke="#cbd5e1"
              />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#2563eb" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard
          className="lg:col-span-2"
          title="Supplier-scope suitability"
          subtitle={`How buyer wording fits a ${config.country}-based supplier`}
          icon={<MapPin className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={340}>
            <BarChart
              data={snapshot.supplierFit}
              layout="vertical"
              margin={{ left: 10, right: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <YAxis
                dataKey="name"
                type="category"
                width={140}
                tick={{ fontSize: 11, fill: "#334155" }}
                stroke="#cbd5e1"
              />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="surface-panel p-6">
          <div className="flex items-center gap-2 mb-3">
            <BadgeCheck className="w-4 h-4 text-primary" />
            <h3 className="font-display text-base">Interpretation guide</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Explicit:</strong> the buyer directly names{" "}
              {config.country} or its nationality.
            </p>
            <p>
              <strong className="text-foreground">Regional:</strong> the buyer accepts a region that
              includes the selected exporter, such as Central Asia for Uzbekistan or Europe/EU for
              Slovakia.
            </p>
            <p>
              <strong className="text-foreground">Worldwide:</strong> broad supplier coverage;
              usually a positive screening signal.
            </p>
            <p>
              <strong className="text-foreground">Not listed:</strong> does not automatically mean
              rejection. Verify with the buyer before excluding the lead.
            </p>
          </div>
        </div>
      </section>
    </DashboardShell>
  );
}
